"use client";
import styles from "../ProductCard.module.css";

import { env } from "@/env.mjs";
import { useLang } from "@/i18n/client";
import { useRouter } from "@/i18n/routing";
import type { ProductCard } from "@/lib/centra";
import { getProductCardProps } from "@/lib/depict/dpc-helpers";
import { getDPC } from "@frend-digital/events/depict";
import { sendGTMEvent } from "@frend-digital/events/gtm";
import { writeBreadcrumbSource } from "@/features/products/breadcrumbSource";
import { clsx } from "@frend-digital/ui";
import { useSetAtom } from "jotai";
import type React from "react";
import { type ComponentProps, type ReactNode, useCallback } from "react";
import { variantAtom } from "./Variants";

type Props = {
	children: ReactNode;
	href: string;
	defaultVariant: string;
	itemListName?: string;
	itemListId?: string;
	productSku?: string;
	productName?: string;
	productPrice?: number;
	collectionPath?: string;
	productId?: string;
} & ComponentProps<"article"> &
	Pick<ProductCard, "depictListingResultId" | "depictSearchResultId" | "depictProductId">;

const ignoredNodes = new Set(["BUTTON", "A"]);

const ProductCardLink = ({
	children,
	href,
	defaultVariant,
	depictSearchResultId,
	depictListingResultId,
	depictProductId,
	itemListName,
	itemListId,
	productSku,
	productName,
	productPrice,
	collectionPath,
	productId,
	...rest
}: Props) => {
	const router = useRouter();
	const setVariant = useSetAtom(variantAtom);
	const lang = useLang();

	const fireSelectItem = useCallback(() => {
		if (!itemListName) return;
		sendGTMEvent({ ecommerce: null });
		sendGTMEvent({
			event: "select_item",
			ecommerce: {
				item_list_name: itemListName,
				item_list_id: itemListId,
				items: [
					{
						item_id: productSku,
						item_name: productName,
						price: productPrice,
					},
				],
			},
		});
	}, [itemListName, itemListId, productSku, productName, productPrice]);

	const persistBreadcrumbSource = useCallback(() => {
		if (collectionPath && productId) {
			writeBreadcrumbSource(productId, collectionPath, lang);
		}
	}, [collectionPath, productId, lang]);

	const handleClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
		(e) => {
			const target = e.target as HTMLElement;
			if (ignoredNodes.has(target.nodeName)) {
				return;
			}

			fireSelectItem();
			router.push(href);
		},
		[router, href, fireSelectItem],
	);

	// Key down handled for accessibility spec
	const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
		(e) => {
			if (e.key === "Enter") {
				persistBreadcrumbSource();
				fireSelectItem();
				router.push(href);
			}
		},
		[router, href, fireSelectItem, persistBreadcrumbSource],
	);

	const handleMouseLeave = useCallback(() => {
		setVariant(defaultVariant);
	}, [defaultVariant, setVariant]);

	return (
		<article
			{...rest}
			data-testid={`product-card-${depictListingResultId ?? depictSearchResultId}`}
			onKeyDown={handleKeyDown}
			onClickCapture={persistBreadcrumbSource}
			onClick={handleClick}
			onMouseLeave={handleMouseLeave}
			className={clsx(styles.root, "product-card")}
			{...getProductCardProps({
				productId: depictProductId,
				listingId: depictListingResultId,
				searchResultId: depictSearchResultId,
			})}
		>
			{children}
		</article>
	);
};
export { ProductCardLink };
